import { Book, MessageSquare, PenTool, Send, Loader2 } from "lucide-react";
import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Mermaid } from '../components/Mermaid';
import { queryKnowledgeBase } from "../lib/knowledgeApi";

interface Message {
  role: "user" | "assistant";
  content: string;
  usage?: any;
}

export function KnowledgeBasePage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const { response, usage } = await queryKnowledgeBase(userMessage);
      setMessages((prev) => [...prev, { role: "assistant", content: response, usage }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `**Error**: No se pudo obtener respuesta. ${(error as Error).message}` }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleStudioAction = async (mode: string, label: string) => {
    if (loading) return;
    const promptMap: Record<string, string> = {
      quiz: "Genera un cuestionario interactivo de 5 preguntas basado en las fuentes.",
      mindmap: "Genera un mapa mental en código Mermaid basado en los documentos.",
      flashcards: "Crea 5 tarjetas didácticas con los conceptos clave.",
    };
    
    const userMessage = promptMap[mode] || label;
    setMessages((prev) => [...prev, { role: "user", content: `[Acción Studio]: ${label}` }]);
    setLoading(true);

    try {
      const { response, usage } = await queryKnowledgeBase(userMessage, mode);
      setMessages((prev) => [...prev, { role: "assistant", content: response, usage }]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `**Error**: No se pudo obtener respuesta. ${(error as Error).message}` }
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page" style={{ height: 'calc(100vh - 80px)' }}>
      <section className="page-intro enter" style={{ paddingBottom: '16px' }}>
        <div>
          <span className="eyebrow">Wolf Notebook</span>
          <h1>Base de Conocimiento (RAG)</h1>
        </div>
      </section>

      <div style={{ display: 'flex', gap: '24px', height: '100%', flex: 1, minHeight: 0 }}>
        {/* Panel Izquierdo: Fuentes */}
        <section className="panel" style={{ flex: '0 0 280px', display: 'flex', flexDirection: 'column' }}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow blue">Fuentes</span>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Book size={16} /> Documentos Activos</h2>
            </div>
          </div>
          <div style={{ padding: '0 20px 20px', flex: 1, overflowY: 'auto' }}>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, gap: '12px', display: 'flex', flexDirection: 'column' }}>
              <li style={{ padding: '12px', background: 'var(--bg)', borderRadius: '6px', border: '1px solid var(--border)', fontSize: '13px' }}>
                <strong>ai_context/notebook_sources/</strong>
                <p style={{ margin: '4px 0 0', color: 'var(--muted)', fontSize: '12px' }}>
                  Todos los archivos .md se cargan dinámicamente como contexto.
                </p>
              </li>
            </ul>
          </div>
        </section>

        {/* Panel Central: Chat */}
        <section className="panel" style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: '400px' }}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow orange">Chat</span>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><MessageSquare size={16} /> Asistente</h2>
            </div>
          </div>
          <div style={{ padding: '0 20px 20px', flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {messages.length === 0 ? (
              <div style={{ margin: 'auto', textAlign: 'center', color: 'var(--muted)' }}>
                <BotIcon />
                <p style={{ marginTop: '12px' }}>Haz una pregunta sobre los manuales de Wolf.</p>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={i} style={{ 
                  alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start',
                  background: m.role === 'user' ? 'var(--indigo)' : 'var(--panel-2)',
                  color: m.role === 'user' ? '#fff' : 'var(--text)',
                  padding: '12px 16px',
                  borderRadius: '12px',
                  maxWidth: '85%',
                  border: m.role === 'user' ? 'none' : '1px solid var(--border)',
                  lineHeight: '1.5',
                  fontSize: '14px',
                  overflowX: 'auto'
                }}>
                  {m.role === 'user' ? (
                    <div style={{ whiteSpace: 'pre-wrap' }}>{m.content}</div>
                  ) : (
                    <div className="markdown-content">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          code({ node, className, children, ...props }: any) {
                            const match = /language-(\w+)/.exec(className || '');
                            const isInline = !match && !String(children).includes('\n');
                            
                            if (!isInline && match && match[1] === 'mermaid') {
                              return <Mermaid chart={String(children).replace(/\n$/, '')} />;
                            }
                            return !isInline ? (
                              <pre style={{ background: '#000', padding: '12px', borderRadius: '6px', overflowX: 'auto', margin: '8px 0' }}>
                                <code className={className} {...props}>
                                  {children}
                                </code>
                              </pre>
                            ) : (
                              <code style={{ background: 'rgba(255,255,255,0.1)', padding: '2px 4px', borderRadius: '4px' }} className={className} {...props}>
                                {children}
                              </code>
                            );
                          },
                          p({ children }) {
                            return <p style={{ margin: '0 0 12px 0', whiteSpace: 'pre-wrap' }}>{children}</p>;
                          },
                          ul({ children }) {
                            return <ul style={{ paddingLeft: '20px', margin: '0 0 12px 0' }}>{children}</ul>;
                          },
                          ol({ children }) {
                            return <ol style={{ paddingLeft: '20px', margin: '0 0 12px 0' }}>{children}</ol>;
                          },
                          h1({ children }) { return <h1 style={{ fontSize: '18px', margin: '16px 0 8px 0' }}>{children}</h1>; },
                          h2({ children }) { return <h2 style={{ fontSize: '16px', margin: '16px 0 8px 0' }}>{children}</h2>; },
                          h3({ children }) { return <h3 style={{ fontSize: '15px', margin: '16px 0 8px 0' }}>{children}</h3>; },
                        }}
                      >
                        {m.content}
                      </ReactMarkdown>
                    </div>
                  )}
                  {m.usage && (
                    <div style={{ marginTop: '12px', fontSize: '11px', color: 'var(--muted)', textAlign: 'right', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                      {m.usage.cached ? (
                        <span>✨ Servido desde Caché (0 Tokens gastados)</span>
                      ) : (
                        <span>
                          Tokens: {m.usage.total_tokens.toLocaleString()} (Prompt: {m.usage.prompt_tokens.toLocaleString()} | Resp: {m.usage.candidates_tokens.toLocaleString()})
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
            {loading && (
              <div style={{ alignSelf: 'flex-start', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Loader2 size={16} style={{ animation: 'spin 2s linear infinite' }} /> Pensando...
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          <div style={{ padding: '20px', borderTop: '1px solid var(--border)' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '12px' }}>
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Pregunta algo sobre la base de conocimiento..."
                disabled={loading}
                style={{ 
                  flex: 1, 
                  background: 'var(--bg)', 
                  border: '1px solid var(--border)', 
                  color: 'var(--text)',
                  padding: '12px 16px',
                  borderRadius: '8px',
                  outline: 'none'
                }}
              />
              <button 
                type="submit" 
                disabled={!input.trim() || loading}
                style={{
                  background: 'var(--teal)',
                  color: '#000',
                  border: 'none',
                  padding: '0 20px',
                  borderRadius: '8px',
                  cursor: input.trim() && !loading ? 'pointer' : 'not-allowed',
                  opacity: input.trim() && !loading ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontWeight: 600
                }}
              >
                <Send size={16} />
              </button>
            </form>
          </div>
        </section>

        {/* Panel Derecho: Studio */}
        <section className="panel" style={{ flex: '0 0 320px', display: 'flex', flexDirection: 'column' }}>
          <div className="panel-heading">
            <div>
              <span className="eyebrow green">Studio</span>
              <h2 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><PenTool size={16} /> Herramientas</h2>
            </div>
          </div>
          <div style={{ padding: '0 20px 20px', flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <p style={{ color: 'var(--muted)', fontSize: '13px', margin: '0 0 8px 0' }}>Transforma el conocimiento de los manuales en distintos formatos interactivos.</p>
            
            <button 
              onClick={() => handleStudioAction('quiz', 'Cuestionario')}
              disabled={loading}
              style={{ padding: '16px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', textAlign: 'left', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, fontWeight: 500 }}
            >
              📝 Cuestionario
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', fontWeight: 400 }}>Evalúa conocimientos con opciones múltiples.</div>
            </button>

            <button 
              onClick={() => handleStudioAction('mindmap', 'Mapa Mental')}
              disabled={loading}
              style={{ padding: '16px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', textAlign: 'left', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, fontWeight: 500 }}
            >
              🧠 Mapa Mental
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', fontWeight: 400 }}>Sintetiza la arquitectura en un diagrama.</div>
            </button>

            <button 
              onClick={() => handleStudioAction('flashcards', 'Tarjetas Didácticas')}
              disabled={loading}
              style={{ padding: '16px', background: 'var(--panel-2)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text)', textAlign: 'left', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.5 : 1, fontWeight: 500 }}
            >
              🃏 Tarjetas Didácticas (Flashcards)
              <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', fontWeight: 400 }}>Memoriza conceptos clave de Data.</div>
            </button>
            
            <div style={{ marginTop: 'auto', padding: '12px', background: 'rgba(242,166,90,0.1)', border: '1px solid rgba(242,166,90,0.3)', borderRadius: '8px', fontSize: '12px', color: 'var(--amber)' }}>
              <strong>Nota:</strong> Los hits repetidos no consumirán cuota (Cached: True).
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}

function BotIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2" />
      <path d="M20 14h2" />
      <path d="M15 13v2" />
      <path d="M9 13v2" />
    </svg>
  );
}
